import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  Button, Form, Input, InputNumber, Modal, Select, Space, Typography,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import DatePicker from 'components/DatePicker/DatePicker';
import { useCreateBrew, useUpdateBrew } from '../hooks';
import * as fmt from '../format';

const { Text } = Typography;

const STATUS_OPTIONS = Object.entries(fmt.BREW_STATUS)
  .map(([value, { label }]) => ({ value, label }));

const toFormValues = (initial) => ({
  status: 'planned',
  ...initial,
  brewDate: initial?.brewDate ? dayjs(initial.brewDate) : null,
  packageDate: initial?.packageDate ? dayjs(initial.packageDate) : null,
  packSplit: initial?.packSplit || [],
});

const fromFormValues = (values) => ({
  beerName: values.beerName,
  brewery: values.brewery,
  volumeLitres: values.volumeLitres,
  brewDate: values.brewDate.format('YYYY-MM-DD'),
  packageDate: values.packageDate ? values.packageDate.format('YYYY-MM-DD') : null,
  status: values.status,
  gyleCode: values.gyleCode || null,
  packSplit: (values.packSplit || []).filter((p) => p && p.sku && p.units > 0),
  notes: values.notes || null,
});

const BrewModal = ({
  open, initial, plan, onClose,
}) => {
  const [form] = Form.useForm();
  const createBrew = useCreateBrew();
  const updateBrew = useUpdateBrew();
  const editing = !!initial?.id;

  useEffect(() => {
    if (open) form.setFieldsValue(toFormValues(initial));
  }, [open, initial, form]);

  const beerName = Form.useWatch('beerName', form);
  const brewery = Form.useWatch('brewery', form);
  const volume = Form.useWatch('volumeLitres', form);
  const packSplit = Form.useWatch('packSplit', form);

  const beer = plan.beers.find((b) => b.beerName === beerName);
  const skuOptions = useMemo(() => (beer ? beer.skuLines : [])
    .filter((l) => !l.viaMix)
    .map((l) => ({ value: l.sku, label: `${l.sku} (${l.unit || 'unit'})`, litresPerUnit: l.litresPerUnit })),
  [beer]);
  const tanks = plan.settings.tanks || [];
  const tankSizes = (tanks.find((t) => t.brewery === brewery) || {}).sizes || [];
  const splitLitres = (packSplit || []).reduce((sum, p) => {
    const opt = skuOptions.find((o) => o.value === p?.sku);
    return sum + (opt && p?.units ? opt.litresPerUnit * p.units : 0);
  }, 0);
  const lossPct = plan.settings.packagingLossPct || 0;
  const packagedTarget = volume ? volume * (1 - lossPct / 100) : null;

  const submit = async () => {
    const values = await form.validateFields();
    const brew = fromFormValues(values);
    if (editing) await updateBrew.mutateAsync({ id: initial.id, ...brew });
    else await createBrew.mutateAsync(brew);
    onClose();
  };

  return (
    <Modal
      title={editing ? `Edit ${initial.beerName} brew` : 'Add brew to the schedule'}
      visible={open}
      onCancel={onClose}
      onOk={submit}
      okText={editing ? 'Save' : 'Add brew'}
      confirmLoading={createBrew.isLoading || updateBrew.isLoading}
      width={720}
      destroyOnClose
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Space align="start" wrap>
          <Form.Item name="beerName" label="Beer" rules={[{ required: true }]}>
            <Select
              showSearch
              style={{ width: 280 }}
              options={plan.beers.map((b) => ({ value: b.beerName, label: b.beerName }))}
            />
          </Form.Item>
          <Form.Item name="brewery" label="Brewery" rules={[{ required: true }]}>
            <Select
              style={{ width: 180 }}
              options={tanks.map((t) => ({ value: t.brewery, label: t.brewery }))}
            />
          </Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select style={{ width: 140 }} options={STATUS_OPTIONS} />
          </Form.Item>
        </Space>
        <Space align="start" wrap>
          <Form.Item
            name="volumeLitres"
            label="Brew volume (L)"
            rules={[{ required: true }]}
            extra={tankSizes.length ? (
              <Space size={4} wrap>
                Tanks:
                {tankSizes.map((size) => (
                  <Button key={size} size="small" type="link" onClick={() => form.setFieldsValue({ volumeLitres: size })}>
                    {fmt.number(size)}
                  </Button>
                ))}
              </Space>
            ) : null}
          >
            <InputNumber min={1} step={500} style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="brewDate" label="Brew date" rules={[{ required: true }]}>
            <DatePicker format="ddd D MMM YYYY" />
          </Form.Item>
          <Form.Item
            name="packageDate"
            label="Ready to sell"
            extra="Blank = brew date + lead time"
          >
            <DatePicker format="ddd D MMM YYYY" />
          </Form.Item>
          <Form.Item name="gyleCode" label="Cin7 gyle" extra="Once brewed, e.g. BR21-2526">
            <Input style={{ width: 150 }} />
          </Form.Item>
        </Space>

        <Form.Item
          label="Pack split (optional)"
          className="forecasting-pack-split-editor"
          extra={packagedTarget ? (
            <Text type={splitLitres > packagedTarget * 1.02 ? 'danger' : 'secondary'}>
              {`${fmt.litres(splitLitres)} of ${fmt.litres(packagedTarget)} packaged `
                + `(after ${lossPct}% loss)`}
            </Text>
          ) : null}
        >
          <Form.List name="packSplit">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Space key={field.key} align="baseline">
                    <Form.Item {...field} name={[field.name, 'sku']} rules={[{ required: true, message: 'SKU' }]}>
                      <Select style={{ width: 340 }} options={skuOptions} placeholder="SKU" />
                    </Form.Item>
                    <Form.Item {...field} name={[field.name, 'units']} rules={[{ required: true, message: 'Units' }]}>
                      <InputNumber min={1} placeholder="Units" style={{ width: 120 }} />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(field.name)} />
                  </Space>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()} disabled={!beer}>
                  Add SKU
                </Button>
              </>
            )}
          </Form.List>
        </Form.Item>

        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

BrewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  initial: PropTypes.shape({
    id: PropTypes.number,
    beerName: PropTypes.string,
    brewDate: PropTypes.string,
    packageDate: PropTypes.string,
    packSplit: PropTypes.arrayOf(PropTypes.object),
  }),
  plan: PropTypes.shape({
    beers: PropTypes.arrayOf(PropTypes.object),
    settings: PropTypes.shape({
      tanks: PropTypes.arrayOf(PropTypes.object),
      packagingLossPct: PropTypes.number,
    }),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

BrewModal.defaultProps = { initial: null };

export default BrewModal;
